// WRPG: every product in this store is digital and sold in exactly one unit
// per cart line, and the two subscription tiers must never sit in the cart
// together (see MembershipState / proxy.membership-products for why). This
// is enforced here, in the single shared add-to-cart submit handler used by
// the product page, the quick-add modal and the collection-grid quick-add
// button, rather than relying on the (hidden) quantity stepper alone - a
// hidden/readonly quantity input does not stop a user from clicking "Add to
// cart" repeatedly, which would otherwise just keep incrementing the line.
let wrpgMembershipProductIdsPromise = null;
function wrpgGetMembershipProductIds() {
  if (!wrpgMembershipProductIdsPromise) {
    wrpgMembershipProductIdsPromise = fetch('/apps/wrpg/membership-products')
      .then((res) => (res.ok ? res.json() : { productIds: [] }))
      .then((data) => (Array.isArray(data.productIds) ? data.productIds.map(String) : []))
      .catch(() => []);
  }
  return wrpgMembershipProductIdsPromise;
}

// Same endpoint the product-page-only membership-guard block
// (extensions/wrpg-crowdfunding/assets/membership-guard.js) already uses -
// duplicated here because this shared submit handler is the *only* guard
// that actually runs for quick-add (collection grid + modal), which never
// loads that block or its script at all. Without this, a customer could
// re-add (and re-pay for) a membership product they're already paid through
// via quick-add, even though the product page itself would block it.
let wrpgMembershipStatusPromise = null;
function wrpgGetMembershipStatus() {
  if (!wrpgMembershipStatusPromise) {
    wrpgMembershipStatusPromise = fetch('/apps/wrpg/membership-status')
      .then((res) => (res.ok ? res.json() : { memberships: [] }))
      .then((data) => (Array.isArray(data.memberships) ? data.memberships : []))
      .catch(() => []);
  }
  return wrpgMembershipStatusPromise;
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async wrpgCartGuardMessage() {
        const productId = this.dataset.productId;
        if (!productId) return null;

        let cart;
        try {
          const cartResponse = await fetch(`${routes.cart_url}.js`);
          cart = await cartResponse.json();
        } catch (e) {
          return null; // don't block a purchase on a network hiccup
        }

        const cartProductIds = (cart.items || []).map((item) => String(item.product_id));

        if (cartProductIds.includes(String(productId))) {
          return this.dataset.i18nAlreadyInCart || 'This product is already in your cart.';
        }

        const membershipIds = await wrpgGetMembershipProductIds();
        if (membershipIds.includes(String(productId))) {
          const hasOtherMembership = cartProductIds.some(
            (id) => membershipIds.includes(id) && id !== String(productId)
          );
          if (hasOtherMembership) {
            return (
              this.dataset.i18nSubscriptionConflict ||
              "You can only have one subscription product in your cart at a time."
            );
          }

          const productGid = `gid://shopify/Product/${productId}`;
          const memberships = await wrpgGetMembershipStatus();
          const activeMembership = memberships.find((entry) => entry.productId === productGid);
          if (activeMembership) {
            const paidThroughAt = new Date(activeMembership.paidThroughAt).getTime();
            if (!isNaN(paidThroughAt) && paidThroughAt > Date.now()) {
              const template =
                this.dataset.i18nAlreadySubscribed ||
                'You already have an active membership on this plan (paid through {date}). It renews automatically unless you cancel it, so there\'s no need to purchase it again.';
              return template.replace('{date}', new Date(activeMembership.paidThroughAt).toLocaleDateString());
            }
          }
        }

        return null;
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const guardMessage = await this.wrpgCartGuardMessage();
        if (guardMessage) {
          if (this.hideErrors || !this.querySelector('.product-form__error-message-wrapper')) {
            window.alert(guardMessage);
          } else {
            this.handleErrorMessage(guardMessage);
          }
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
          // WRPG: when adding from the cart page (upsell card / variant
          // modal), bundle the cart page's own sections into the POST
          // response so items and totals update atomically. A separate GET
          // refresh can race Shopify's cart persistence and render the
          // pre-add state.
          // Only the items + footer sections: Shopify's bundled section
          // rendering caps a request at 5 sections and silently returns
          // "sections": null above that (notification already needs 3).
          const pageCartItems = document.querySelector('cart-items');
          if (pageCartItems && typeof pageCartItems.getSectionsToRender === 'function') {
            pageCartItems
              .getSectionsToRender()
              .filter((section) => section.id === 'main-cart-items' || section.id === 'main-cart-footer')
              .forEach((section) => sectionIds.push(section.section));
          }
          formData.append('sections', [...new Set(sectionIds)]);
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (this.dataset.wrpgRedirectToCheckout === 'true') {
              // Opt-in escape hatch for "Subscribe Now"-style buttons (e.g.
              // the homepage subscription cards) that should skip the cart
              // page/drawer entirely. Dawn's AJAX add-to-cart makes the
              // classic return_to=/checkout hidden-field trick a no-op here,
              // since nothing ever does a real browser form submission.
              window.location.href = '/checkout';
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            // The cart page (if present) refreshes via the cartUpdate
            // subscriber in cart.js, which consumes response.sections.
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
            this.wrpgMaybeOpenDealModal();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      wrpgMaybeOpenDealModal() {
        if (!this.form || !this.form.classList.contains('wrpg-subscriber-deal-form')) return;
        const audience = this.form.dataset.wrpgDealAudience || 'subscriber';
        if (audience === 'public') {
          // Public deals already apply to any identified customer via the
          // automatic discount's customer segments - only a guest (never
          // tagged/segmented) needs the login nudge.
          if (document.documentElement.getAttribute('data-wrpg-logged-in') === '1') return;
        } else if (document.documentElement.getAttribute('data-wrpg-member') === '1') {
          return;
        }
        document.dispatchEvent(new CustomEvent('wrpg:subscriber-deal-added', { detail: { audience } }));
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
